import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Tooltip,
    useTheme,
    useMediaQuery,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemButton
} from '@mui/material';
import {
    BarChart as BarChartIcon,
    Close as CloseIcon,
    ChevronRight as ChevronRightIcon,
    Person as PersonIcon,
    Refresh as RefreshIcon,
    Layers as LayersIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getAggregatedArchetypeStatsSnapshot, refreshAggregatedArchetypeStats, getAllArchetypes, getMatchupBreakdown } from '../../services/tracker-data';
import { InkColorIcons } from './InkColorIcon';

function ArchetypeStatsMatrix({ trackerFilters }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    const [archetypeStats, setArchetypeStats] = useState({});
    const [allArchetypes, setAllArchetypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCell, setSelectedCell] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [matchupDecks, setMatchupDecks] = useState([]);
    const [matchupPlayers, setMatchupPlayers] = useState([]);
    const [matchupLoading, setMatchupLoading] = useState(false);
    const [statsMeta, setStatsMeta] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [showAdvancedMatrix, setShowAdvancedMatrix] = useState(!isMobile);

    useEffect(() => {
        if (!selectedCell || !currentUser) {
            setMatchupDecks([]);
            setMatchupPlayers([]);
            return;
        }
        const loadMatchupBreakdown = async () => {
            setMatchupLoading(true);
            try {
                const data = await getMatchupBreakdown(
                    selectedCell.archetype.id,
                    selectedCell.opponentArchetypeId,
                    trackerFilters
                );
                setMatchupDecks(data?.decks || []);
                setMatchupPlayers(data?.players || []);
            } catch (err) {
                console.error('Błąd podczas ładowania danych matchup:', err);
                setMatchupDecks([]);
                setMatchupPlayers([]);
            } finally {
                setMatchupLoading(false);
            }
        };
        loadMatchupBreakdown();
    }, [selectedCell, currentUser, trackerFilters]);


    const loadArchetypeStats = useCallback(async () => {
        if (!currentUser) {
            setArchetypeStats({});
            setAllArchetypes([]);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const [stats, archetypes] = await Promise.all([
                getAggregatedArchetypeStatsSnapshot(trackerFilters),
                getAllArchetypes(trackerFilters)
            ]);
            setArchetypeStats(stats?.stats || stats || {});
            setStatsMeta(stats?.meta || null);
            setAllArchetypes(archetypes || []);
        } catch (err) {
            console.error('Błąd podczas ładowania statystyk archetypów:', err);
            setError('Nie udało się załadować statystyk archetypów');
        } finally {
            setLoading(false);
        }
    }, [currentUser, trackerFilters]);

    useEffect(() => {
        loadArchetypeStats();
    }, [loadArchetypeStats]);

    useEffect(() => {
        setShowAdvancedMatrix(!isMobile);
    }, [isMobile]);

    const handleRefresh = async () => {
        try {
            setRefreshing(true);
            setError(null);
            const payload = await refreshAggregatedArchetypeStats(trackerFilters);
            setArchetypeStats(payload?.stats || {});
            setStatsMeta(payload?.meta || null);
        } catch (err) {
            console.error('Błąd podczas odświeżania statystyk archetypów:', err);
            setError('Nie udało się odświeżyć danych matrixa.');
        } finally {
            setRefreshing(false);
        }
    };

    const getWinrateColor = (winrate) => {
        if (winrate >= 60) return '#10b981';
        if (winrate >= 50) return '#f59e0b';
        return '#ef4444';
    };

    const getWinrateBackgroundColor = (winrate) => {
        if (winrate >= 60) return 'rgba(16, 185, 129, 0.2)';
        if (winrate >= 50) return 'rgba(245, 158, 11, 0.2)';
        return 'rgba(239, 68, 68, 0.2)';
    };

    const handleCellClick = (archetypeId, opponentArchetypeId) => {
        const archetype = archetypeStats[archetypeId];
        if (archetype && archetype.vsArchetypes && archetype.vsArchetypes[opponentArchetypeId]) {
            setSelectedCell({
                archetype: { id: archetypeId, ...archetype },
                opponentArchetype: archetype.vsArchetypes[opponentArchetypeId],
                opponentArchetypeId: opponentArchetypeId
            });
            setDialogOpen(true);
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedCell(null);
    };

    const matrixSummary = useMemo(() => {
        const rowMetrics = {};
        const colGames = {};
        Object.entries(archetypeStats).forEach(([id, archetype]) => {
            const vsEntries = Object.values(archetype.vsArchetypes || {});
            const games = vsEntries.reduce((sum, value) => sum + (value.games || 0), 0);
            const wins = vsEntries.reduce((sum, value) => sum + (value.wins || 0), 0);
            rowMetrics[id] = {
                games,
                wins,
                winrate: games > 0 ? (wins / games) * 100 : 0
            };
            Object.entries(archetype.vsArchetypes || {}).forEach(([oppId, value]) => {
                colGames[oppId] = (colGames[oppId] || 0) + (value.games || 0);
            });
        });
        return { rowMetrics, colGames };
    }, [archetypeStats]);

    const sortedEntries = useMemo(
        () => Object.entries(archetypeStats).sort((a, b) => (matrixSummary.rowMetrics[b[0]]?.games || 0) - (matrixSummary.rowMetrics[a[0]]?.games || 0)),
        [archetypeStats, matrixSummary]
    );

    const sortedArchetypes = useMemo(() => [...allArchetypes]
        .sort((a, b) => {
            const gamesA = matrixSummary.colGames[a.id] || 0;
            const gamesB = matrixSummary.colGames[b.id] || 0;
            if (gamesB !== gamesA) return gamesB - gamesA;
            return (a.name || '').localeCompare(b.name || '');
        })
        .map((a) => ({
            id: a.id,
            name: a.name || 'Nieznany archetyp',
            colors: a.ink_colors || a.colors || []
        })), [allArchetypes, matrixSummary]);

    const topMobileRows = useMemo(
        () => sortedEntries
            .slice(0, 6)
            .map(([id, archetype]) => ({
                id,
                name: archetype.name || 'Nieznany archetyp',
                colors: archetype.colors || [],
                games: matrixSummary.rowMetrics[id]?.games || 0,
                winrate: matrixSummary.rowMetrics[id]?.winrate || 0
            })),
        [sortedEntries, matrixSummary]
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress sx={{ color: '#7c3aed' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: 'white'
            }}>
                {error}
            </Alert>
        );
    }

    if (Object.keys(archetypeStats).length === 0) {
        return (
            <Card sx={{
                background: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '16px'
            }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <BarChartIcon sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: 64, mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                        Brak danych o archetypach
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                        Statystyki archetypów będą dostępne po zarejestrowaniu meczów
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                    Matchupy
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                    Globalne statystyki matchupów między archetypami. Kliknij komórkę, aby zobaczyć szczegóły.
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.5)', display: 'block', mt: 0.5 }}>
                    Matrix: wiersze to twój archetyp, kolumny to archetyp przeciwnika.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 1 }}>
                    <Chip size="small" label="60%+" sx={{ bgcolor: 'rgba(16, 185, 129, 0.25)', color: '#10b981', fontWeight: 700 }} />
                    <Chip size="small" label="50-59%" sx={{ bgcolor: 'rgba(245, 158, 11, 0.25)', color: '#f59e0b', fontWeight: 700 }} />
                    <Chip size="small" label="<50%" sx={{ bgcolor: 'rgba(239, 68, 68, 0.25)', color: '#ef4444', fontWeight: 700 }} />
                </Box>
                <Box sx={{ mt: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.65)' }}>
                        Dane wspólne: {statsMeta?.sharedGeneratedAt ? new Date(statsMeta.sharedGeneratedAt).toLocaleString('pl-PL') : 'brak'}
                    </Typography>
                    <Button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        startIcon={<RefreshIcon />}
                        size="small"
                        variant="outlined"
                    >
                        {refreshing ? 'Odświeżanie...' : 'Odśwież dane'}
                    </Button>
                </Box>
            </Box>

            {isMobile && !showAdvancedMatrix ? (
                <Card sx={{
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '1px solid rgba(76, 29, 149, 0.3)',
                    borderRadius: '12px'
                }}>
                    <CardContent sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: 'rgba(196, 181, 253, 0.85)', mb: 1.5 }}>
                            Najaktywniejsze archetypy
                        </Typography>
                        <List sx={{ p: 0 }}>
                            {topMobileRows.map((row) => (
                                <ListItem key={row.id} disablePadding sx={{ mb: 0.8 }}>
                                    <ListItemButton onClick={() => navigate(`/archetype/${row.id}`)} sx={{ borderRadius: 1.5, bgcolor: 'rgba(17, 24, 39, 0.4)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.2 }}>
                                            <InkColorIcons colors={row.colors} size={18} gap={0.3} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ color: 'white' }} noWrap>
                                                    {row.name}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)' }}>
                                                    {row.games} gier
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ color: getWinrateColor(row.winrate), fontWeight: 700 }}>
                                                {row.winrate.toFixed(1)}%
                                            </Typography>
                                        </Box>
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                        <Button fullWidth variant="outlined" onClick={() => setShowAdvancedMatrix(true)} sx={{ mt: 1, textTransform: 'none' }}>
                            Pokaż pełną tabelę
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <Card sx={{
                    background: 'rgba(17, 24, 39, 0.8)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(76, 29, 149, 0.3)',
                    borderRadius: '16px',
                    overflow: 'hidden'
                }}>
                    <CardContent sx={{ p: 0 }}>
                        {isMobile && (
                            <Box sx={{ p: 1.25, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>
                                <Button size="small" variant="text" onClick={() => setShowAdvancedMatrix(false)} sx={{ textTransform: 'none' }}>
                                    Wróć do skrótu mobilnego
                                </Button>
                            </Box>
                        )}
                        <TableContainer sx={{ maxHeight: isMobile ? 400 : 600 }}>
                            <Table stickyHeader size={isSmallMobile ? "small" : "medium"}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{
                                        backgroundColor: 'rgba(17, 24, 39, 0.95)',
                                        color: 'rgba(196, 181, 253, 0.7)',
                                        fontWeight: 600,
                                        borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                                        minWidth: isSmallMobile ? 120 : 150,
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 10
                                    }}>
                                        Archetyp
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            backgroundColor: 'rgba(76, 29, 149, 0.2)',
                                            color: 'rgba(196, 181, 253, 0.9)',
                                            fontWeight: 600,
                                            borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                                            textAlign: 'center',
                                            minWidth: isSmallMobile ? 90 : 110
                                        }}
                                    >
                                        <Tooltip title="Suma wszystkich matchupów w wierszu" arrow>
                                            <Typography variant="caption" sx={{ fontSize: isSmallMobile ? '0.6rem' : '0.7rem' }}>
                                                Podsumowanie
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    {sortedArchetypes.map((archetype) => (
                                        <TableCell
                                            key={archetype.id}
                                            onClick={() => navigate(`/archetype/${archetype.id}`)}
                                            sx={{
                                                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                                                color: 'rgba(196, 181, 253, 0.7)',
                                                fontWeight: 600,
                                                borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                                                textAlign: 'center',
                                                minWidth: isSmallMobile ? 80 : 100,
                                                maxWidth: isSmallMobile ? 80 : 100,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                        >
                                            <Tooltip title={archetype.name} arrow>
                                                <Box>
                                                    <Typography variant="caption" sx={{
                                                        display: 'block',
                                                        fontSize: isSmallMobile ? '0.65rem' : '0.75rem',
                                                        lineHeight: 1.2
                                                    }}>
                                                        {archetype.name}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                                                        <InkColorIcons
                                                            colors={archetype.colors}
                                                            size={isSmallMobile ? 16 : 20}
                                                            gap={0.3}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedEntries.map(([archetypeId, archetype]) => (
                                    <TableRow key={archetypeId}>
                                        <TableCell
                                            onClick={() => navigate(`/archetype/${archetypeId}`)}
                                            sx={{
                                                backgroundColor: 'rgba(17, 24, 39, 0.8)',
                                                color: 'white',
                                                borderBottom: '1px solid rgba(76, 29, 149, 0.2)',
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 5,
                                                minWidth: isSmallMobile ? 120 : 150,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                                    transform: 'translateX(2px)'
                                                }
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" sx={{
                                                    fontWeight: 600,
                                                    fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                                                    mb: 0.5
                                                }}>
                                                    {archetype.name || 'Nieznany archetyp'}
                                                </Typography>
                                                <InkColorIcons
                                                    colors={archetype.colors || []}
                                                    size={isSmallMobile ? 16 : 20}
                                                    gap={0.3}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                backgroundColor: 'rgba(76, 29, 149, 0.15)',
                                                color: 'white',
                                                borderBottom: '1px solid rgba(76, 29, 149, 0.2)',
                                                textAlign: 'center',
                                                minWidth: isSmallMobile ? 90 : 110
                                            }}
                                        >
                                            {(matrixSummary.rowMetrics[archetypeId]?.games || 0) > 0 ? (
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: getWinrateColor(matrixSummary.rowMetrics[archetypeId]?.winrate || 0),
                                                            fontWeight: 700,
                                                            fontSize: isSmallMobile ? '0.75rem' : '0.875rem'
                                                        }}
                                                    >
                                                        {(matrixSummary.rowMetrics[archetypeId]?.winrate || 0).toFixed(1)}%
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'rgba(196, 181, 253, 0.7)',
                                                            fontSize: isSmallMobile ? '0.6rem' : '0.7rem'
                                                        }}
                                                    >
                                                        {matrixSummary.rowMetrics[archetypeId]?.games || 0} gier
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" sx={{ color: 'rgba(156, 163, 175, 0.5)' }}>—</Typography>
                                            )}
                                        </TableCell>
                                        {sortedArchetypes.map((opponentArchetype) => {
                                            const vsStats = archetype.vsArchetypes?.[opponentArchetype.id];
                                            const winrate = vsStats?.winrate || 0;
                                            const games = vsStats?.games || 0;

                                            return (
                                                <TableCell
                                                    key={opponentArchetype.id}
                                                    sx={{
                                                        backgroundColor: games > 0 ? getWinrateBackgroundColor(winrate) : 'rgba(17, 24, 39, 0.3)',
                                                        color: 'white',
                                                        borderBottom: '1px solid rgba(76, 29, 149, 0.2)',
                                                        textAlign: 'center',
                                                        cursor: games > 0 ? 'pointer' : 'default',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': games > 0 ? {
                                                            backgroundColor: getWinrateBackgroundColor(winrate),
                                                            transform: 'scale(1.05)',
                                                            boxShadow: `0 0 10px ${getWinrateColor(winrate)}40`
                                                        } : {},
                                                        minWidth: isSmallMobile ? 80 : 100,
                                                        maxWidth: isSmallMobile ? 80 : 100
                                                    }}
                                                    onClick={() => games > 0 && handleCellClick(archetypeId, opponentArchetype.id)}
                                                >
                                                    {games > 0 ? (
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: getWinrateColor(winrate),
                                                                    fontWeight: 700,
                                                                    fontSize: isSmallMobile ? '0.75rem' : '0.875rem'
                                                                }}
                                                            >
                                                                {winrate.toFixed(1)}%
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'rgba(196, 181, 253, 0.7)',
                                                                    fontSize: isSmallMobile ? '0.6rem' : '0.7rem'
                                                                }}
                                                            >
                                                                {games} gier
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'rgba(156, 163, 175, 0.5)',
                                                                fontSize: isSmallMobile ? '0.6rem' : '0.7rem'
                                                            }}
                                                        >
                                                            --
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
                </Card>
            )}

            {/* Dialog ze szczegółowymi statystykami */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: 'rgba(17, 24, 39, 0.95)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(76, 29, 149, 0.3)',
                        borderRadius: '16px'
                    }
                }}
            >
                <DialogTitle sx={{
                    color: 'white',
                    borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Szczegółowe Statystyki
                    </Typography>
                    <IconButton onClick={handleCloseDialog} sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3, pt: 4 }}>
                    {selectedCell && (
                        <Box>
                            {/* Matchup header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3, mt: 1 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                    <InkColorIcons colors={selectedCell.archetype.colors || []} size={28} gap={0.5} />
                                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.95)', fontWeight: 600, textAlign: 'center', maxWidth: 120 }} noWrap>{selectedCell.archetype.name}</Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.4)', fontWeight: 600, fontSize: '0.7rem' }}>VS</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                    <InkColorIcons colors={selectedCell.opponentArchetype.colors || []} size={28} gap={0.5} />
                                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.95)', fontWeight: 600, textAlign: 'center', maxWidth: 120 }} noWrap>{selectedCell.opponentArchetype.name}</Typography>
                                </Box>
                            </Box>

                            {/* Stats - compact hero block */}
                            <Box sx={{ bgcolor: 'rgba(17, 24, 39, 0.5)', borderRadius: 2, p: 2, mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                    <Box>
                                        <Typography variant="h3" sx={{ color: getWinrateColor(selectedCell.opponentArchetype.winrate), fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                                            {selectedCell.opponentArchetype.winrate.toFixed(1)}%
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.5)' }}>winrate</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h6" sx={{ color: '#10b981', fontWeight: 700 }}>{selectedCell.opponentArchetype.wins}</Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.5)' }}>W</Typography>
                                        </Box>
                                        <Typography sx={{ color: 'rgba(196, 181, 253, 0.3)', fontWeight: 300 }}>—</Typography>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h6" sx={{ color: '#ef4444', fontWeight: 700 }}>{selectedCell.opponentArchetype.losses}</Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.5)' }}>L</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                {selectedCell.opponentArchetype.play && selectedCell.opponentArchetype.draw && (
                                    <Box sx={{ display: 'flex', gap: 2, mt: 2, pt: 2, borderTop: '1px solid rgba(76, 29, 149, 0.15)' }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)' }}>
                                            Play <Box component="span" sx={{ color: getWinrateColor(selectedCell.opponentArchetype.play.winrate), fontWeight: 600 }}>{selectedCell.opponentArchetype.play.winrate.toFixed(0)}%</Box>
                                            <Box component="span" sx={{ color: 'rgba(196, 181, 253, 0.4)', ml: 0.5 }}>({selectedCell.opponentArchetype.play.games}g)</Box>
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)' }}>
                                            Draw <Box component="span" sx={{ color: getWinrateColor(selectedCell.opponentArchetype.draw.winrate), fontWeight: 600 }}>{selectedCell.opponentArchetype.draw.winrate.toFixed(0)}%</Box>
                                            <Box component="span" sx={{ color: 'rgba(196, 181, 253, 0.4)', ml: 0.5 }}>({selectedCell.opponentArchetype.draw.games}g)</Box>
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {matchupLoading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={24} sx={{ color: 'rgba(124, 58, 237, 0.6)' }} />
                                </Box>
                            )}

                            {/* Per-talia lista */}
                            {!matchupLoading && matchupDecks.length > 0 && (
                                <Box sx={{ mb: matchupPlayers.length > 0 ? 3 : 0 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'rgba(196, 181, 253, 0.6)', mb: 1.5, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                        Per talia
                                    </Typography>
                                    <List sx={{ p: 0 }}>
                                        {matchupDecks.map((deck) => {
                                            const isOwn = deck.playerId === currentUser?.uid;
                                            const inner = (
                                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
                                                    <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: 'rgba(76, 29, 149, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        <LayersIcon sx={{ color: 'rgba(124, 58, 237, 0.55)', fontSize: 20 }} />
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }} noWrap>{deck.name}</Typography>
                                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.45)', fontSize: '0.7rem' }} noWrap>
                                                            {deck.playerName} · {deck.wins}W {deck.losses}L
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" sx={{ color: getWinrateColor(deck.winrate), fontWeight: 700, fontSize: '0.95rem', flexShrink: 0 }}>
                                                        {deck.winrate.toFixed(1)}%
                                                    </Typography>
                                                    {isOwn && <ChevronRightIcon sx={{ color: 'rgba(196, 181, 253, 0.35)', fontSize: 18, flexShrink: 0 }} />}
                                                </Box>
                                            );
                                            return (
                                                <ListItem key={deck.deckId} disablePadding sx={{ mb: 0.75 }}>
                                                    {isOwn ? (
                                                        <ListItemButton
                                                            onClick={() => { handleCloseDialog(); navigate(`/tracker/${deck.deckId}`); }}
                                                            sx={{ borderRadius: 1.5, py: 1.25, px: 1.5, bgcolor: 'rgba(17, 24, 39, 0.3)', transition: 'all 0.2s ease', '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.12)' } }}
                                                        >
                                                            {inner}
                                                        </ListItemButton>
                                                    ) : (
                                                        <Box sx={{ borderRadius: 1.5, py: 1.25, px: 1.5, bgcolor: 'rgba(17, 24, 39, 0.3)', width: '100%' }}>
                                                            {inner}
                                                        </Box>
                                                    )}
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Box>
                            )}

                            {/* Per-gracz lista */}
                            {!matchupLoading && matchupPlayers.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: 'rgba(196, 181, 253, 0.6)', mb: 1.5, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                        Per gracz
                                    </Typography>
                                    <List sx={{ p: 0 }}>
                                        {matchupPlayers.map((player) => (
                                            <ListItem key={player.playerId} disablePadding sx={{ mb: 0.75 }}>
                                                <Box
                                                    sx={{
                                                        borderRadius: 1.5,
                                                        py: 1.25,
                                                        px: 1.5,
                                                        bgcolor: player.playerId === currentUser?.uid ? 'rgba(124, 58, 237, 0.08)' : 'rgba(17, 24, 39, 0.3)',
                                                        border: player.playerId === currentUser?.uid ? '1px solid rgba(124, 58, 237, 0.2)' : '1px solid transparent',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                        gap: 1.5
                                                    }}
                                                >
                                                    <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: 'rgba(76, 29, 149, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        <PersonIcon sx={{ color: 'rgba(124, 58, 237, 0.5)', fontSize: 20 }} />
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }} noWrap>{player.name}</Typography>
                                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.45)', fontSize: '0.7rem' }}>
                                                            {player.wins}W {player.losses}L · {player.games} gier
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" sx={{ color: getWinrateColor(player.winrate), fontWeight: 700, fontSize: '0.95rem', flexShrink: 0 }}>
                                                        {player.winrate.toFixed(1)}%
                                                    </Typography>
                                                </Box>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(76, 29, 149, 0.3)' }}>
                    <Button
                        onClick={handleCloseDialog}
                        sx={{
                            color: 'rgba(196, 181, 253, 0.7)',
                            borderColor: 'rgba(76, 29, 149, 0.5)',
                            '&:hover': {
                                borderColor: '#7c3aed',
                                backgroundColor: 'rgba(124, 58, 237, 0.1)'
                            }
                        }}
                        variant="outlined"
                    >
                        Zamknij
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ArchetypeStatsMatrix;
